import React from "react";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { RefreshCw, TriangleAlert } from "lucide-react";
import { getLogger } from "@/utils/logger";
import * as Sentry from "@sentry/react";
import { isStaleChunkError, reloadForStaleChunk } from "@/utils/staleChunkReload";

const log = getLogger("AppRootErrorBoundary");

class AppRootErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      hasError: false,
      isStaleChunk: false,
      retryCount: 0,
    };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, isStaleChunk: isStaleChunkError(error) };
  }

  componentDidCatch(error, errorInfo) {
  log.error(
    "[AppRootErrorBoundary:componentDidCatch] > [Error]: uncaught component error",
    error,
    { componentStack: errorInfo?.componentStack || "" }
  );
  Sentry.captureException(error, {
    contexts: { react: { componentStack: errorInfo?.componentStack || "" } },
  });
  if (isStaleChunkError(error)) {
    reloadForStaleChunk("AppRootErrorBoundary");
  }
}

  handleRetry = () => {
    if (this.state.isStaleChunk) {
      // A boundary reset would re-import the same dead chunk URL from the old
      // build; only a full reload picks up the latest deploy.
      log.info("[AppRootErrorBoundary:handleRetry] > [Request]: full reload for stale chunk");
      window.location.reload();
      return;
    }
    log.info("[AppRootErrorBoundary:handleRetry] > [Request]: reset application boundary");
    this.setState((previousState) => ({
      hasError: false,
      retryCount: previousState.retryCount + 1,
    }));
    log.info("[AppRootErrorBoundary:handleRetry] > [Response]: boundary reset complete");
  };

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-white px-4">
          <div className="w-full max-w-lg space-y-4">
            <Alert className="border-[#324c48]/20">
              <TriangleAlert className="h-4 w-4 text-[#324c48]" />
              <AlertTitle className="text-[#324c48]">Something went wrong</AlertTitle>
              <AlertDescription className="text-sm text-gray-600">
                The app hit an unexpected error. You can retry without losing this browser session.
              </AlertDescription>
            </Alert>
            <Button
              onClick={this.handleRetry}
              className="bg-[#324c48] text-white"
              id="app-root-error-boundary-btn-retry"
            >
              Retry
              <RefreshCw className="ml-2 h-4 w-4" />
            </Button>
          </div>
        </div>
      );
    }

    return <React.Fragment key={this.state.retryCount}>{this.props.children}</React.Fragment>;
  }
}

export default AppRootErrorBoundary;
